import { useContext, useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { IFigureDto } from '../models/IFigureDto';
import missingImg from '../assets/not_found.png';
import { FavoritesContext } from '../context/FavoritesContext';
import { MoreVertMenu } from './MoreVertMenu';
import { StatusChip, StatusCode } from './StatusChip';
import DataDetails from './DataDetails';
import { formatNorwegianDate, sanitizeHtml } from '../utils/formatters';
import CollectionAvatars from './CollectionAvatars';
import Series from './Series';
import { useAppEvent } from '../hooks/useAppEvent';

interface ListViewItemProps {
  figure: IFigureDto;
  collectionId: number;
  onFigureSelect: (figure: IFigureDto) => void;
  onAvatarClick: (collectionId: number) => void;
  isDefaultCollection?: boolean;
  collections?: Array<{ id: number; name: string; color?: string }>;
}

const ListViewItem: React.FC<ListViewItemProps> = ({
  figure: initialFigure,
  collectionId,
  onFigureSelect,
  onAvatarClick,
  isDefaultCollection = false,
  collections = [],
}) => {
  const { toggleFavorite, isFavorite } = useContext(FavoritesContext);
  const [figure, setFigure] = useState(initialFigure);

  // Use FavoritesContext to reflect global favorite state
  const isFav = isFavorite(figure.imageId.toString());

  useAppEvent('figureUpdated', (updatedFigure) => {
    if (updatedFigure.imageId !== figure.imageId) return;
    // eslint-disable-next-line no-console
    console.log(`ListViewItem: Updated figure ${updatedFigure.imageId}, customerFavorite=${updatedFigure.customerFavorite}`);
    setFigure(updatedFigure);
  });

  useAppEvent('figureRemoved', ({ removedFigureId, collectionId: removedFrom }) => {
    if (removedFigureId !== figure.imageId) return;
    setFigure((prev) => {
      const next = { ...prev, collectionMemberships: { ...prev.collectionMemberships } };
      delete next.collectionMemberships[Number(removedFrom)];
      return next;
    });
  });

  // Update figure when props change
  useEffect(() => {
    setFigure(initialFigure);
  }, [initialFigure]);

  const safeStatus = [0, 1, 2, 3, 4].includes(figure.status) ? (figure.status as StatusCode) : (0 as StatusCode);

  // Choose the best available file for a small thumbnail
  const thumbnailFileName =
    (figure.defaultFile && String(figure.defaultFile)) ||
    (figure.imgName && String(figure.imgName)) ||
    (figure.zoomFile && String(figure.zoomFile)) ||
    '';

  const hasThumbExtension = typeof thumbnailFileName === 'string' && /\.[^/.]+$/.test(thumbnailFileName);
  const thumbnailSrc = hasThumbExtension
    ? `/image?docNumber=${figure.docNumber}&versionId=${figure.versionId}&fileName=${thumbnailFileName}`
    : missingImg;

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await toggleFavorite(figure.imageId.toString(), collectionId);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  return (
    <Paper
      onClick={(e) => {
        // Only trigger figure selection if the click wasn't on the StatusChip or its dialog
        if (!(e.target as HTMLElement).closest('.MuiDialog-root') && !(e.target as HTMLElement).closest('.MuiChip-root')) {
          onFigureSelect(figure);
        }
      }}
      elevation={0}
      sx={{
        display: 'flex',
        p: 1,
        gap: 1,
        alignItems: 'center',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 56px 1fr 220px 110px 30px 20px 20px',
          alignItems: 'center',
          columnGap: 0.25,
          width: '100%',
        }}>
        {/* Series Icon */}
        <IconButton size="small" sx={{ padding: '4px', justifySelf: 'center' }}>
          <Series seriesId={figure.seriesId} size={20} />
        </IconButton>

        {/* Mini Thumbnail */}
        <Box
          sx={{
            width: '56px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
          <img src={thumbnailSrc} alt={figure.imgName || ''} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>

        {/* Description Text */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(figure.captionHtml ?? '', 980),
          }}
        />

        {/* DataDetails - ANVISNING, VERSJON, PUBLISERT with headers */}
        <Box sx={{ overflow: 'hidden', minWidth: 0, width: '100%', alignSelf: 'start', padding: 0, margin: 0 }}>
          <DataDetails
            docNumber={figure.docNumber ?? ''}
            guideVersionPublicId={figure.guideVersionPublicId ?? ''}
            publishedDate={figure.publishedDate?.toString()}
            imgName={figure.imgName}
            small={true}
          />
        </Box>

        {/* Collection Avatars */}
        <Box sx={{ overflow: 'hidden', justifySelf: 'end' }}>
          {isDefaultCollection ? (
            // For default collection, show all collection memberships (max 3)
            <CollectionAvatars
              collections={Object.entries(figure.collectionMemberships).map(([id, name]) => {
                const membershipCollectionId = Number(id);
                const collection = collections.find((c) => c.id === membershipCollectionId);
                return { id, name, color: collection?.color };
              })}
              variant="small"
              maxVisible={3}
              onAvatarClick={onAvatarClick}
              onOverflowClick={() => onFigureSelect(figure)}
            />
          ) : (
            // For other collections, show single avatar for current collection
            (() => {
              const currentCollection = collections.find((c) => c.id === collectionId);
              if (currentCollection) {
                return (
                  <CollectionAvatars
                    collections={[{ id: String(collectionId), name: currentCollection.name, color: currentCollection.color }]}
                    variant="small"
                    maxVisible={1}
                    onAvatarClick={onAvatarClick}
                  />
                );
              }
              return null;
            })()
          )}
        </Box>

        {/* Status Chip */}
        <Box sx={{ justifySelf: 'end', pr: 0.25 }}>
          <StatusChip
            status={safeStatus}
            showLabel={false}
            lastUpdated={formatNorwegianDate(figure.publishedDate?.toString())}
            published={formatNorwegianDate(figure.createdDate?.toString())}
            description="Some text here please - from where?"
          />
        </Box>

        {/* Favorite Button */}
        <IconButton
          size="small"
          onClick={handleFavoriteClick}
          sx={{
            color: isFav ? 'error.main' : 'inherit',
            '&:hover': { color: 'error.main' },
            padding: '0px',
            justifySelf: 'end',
            marginRight: '-4px',
          }}>
          {isFav ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>

        {/* More Menu */}
        <IconButton size="small" sx={{ padding: '0px', justifySelf: 'start', marginLeft: '-6px' }}>
          <MoreVertMenu
            onEdit={() => onFigureSelect(figure)}
            onHelp={() => {}}
            customerId={1}
            figureId={figure.imageId}
            collectionId={collectionId}
          />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ListViewItem;
